import {useState} from "react";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useDndMonitor} from "@dnd-kit/core";
import {useTranslation} from "react-i18next";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import DraggableReservationRow from "./DraggableReservationRow";
import {TableOverlay} from "../../types";
import {TABLE_RESERVATION_COLUMN_WIDTHS} from "./tableReservationDialogLayout";

interface Props {
    open: boolean;
    overlay: TableOverlay;
    tableId: string;
    reservations: FloorplanReservationItemDTO[];
    onClickEdit?: (reservation: Reservation) => void;
    onClose: () => void;
}

const ViewTableReservationsDialog: React.FC<Props> = ({
                                                          open,
                                                          overlay,
                                                          tableId,
                                                          reservations,
                                                          onClickEdit,
                                                          onClose,
                                                      }) => {
    const {t} = useTranslation();
    const [isDraggingOne, setIsDraggingOne] = useState(false);

    useDndMonitor({
        onDragStart({active}) {
            const id = active.id?.toString();
            if (id?.startsWith("dialog-")) {
                setIsDraggingOne(true);
            }
        },
        onDragEnd({active}) {
            const id = active.id?.toString();
            if (id?.startsWith("dialog-")) {
                onClose();
            }
            setIsDraggingOne(false);
        },
        onDragCancel() {
            setIsDraggingOne(false);
        },
    });

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown={isDraggingOne}
            hideBackdrop={isDraggingOne}
            onClose={() => {
                if (!isDraggingOne) onClose();
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    opacity: isDraggingOne ? 0 : 1,
                    pointerEvents: isDraggingOne ? "none" : "auto",
                    transition: "opacity 150ms ease-in-out",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                {overlay.resource.name}
                <IconButton
                    aria-label={t("Close")}
                    edge="end"
                    size="small"
                    onClick={onClose}
                >
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxHeight: 500,
                    overflowY: "auto",
                }}
            >
                {reservations.length === 0 ? (
                    <Box sx={{p: 4, textAlign: 'center'}}>
                        <Typography color="text.secondary">
                            {t("No reservations for this table")}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            width="100%"
                            sx={{
                                borderBottom: "2px solid #ccc",
                                px: 1,
                                py: 0.5,
                                fontWeight: "bold",
                            }}
                        >
                            <Typography variant="subtitle1" sx={{flex: 1, minWidth: 0}}>
                                {t("Guests")}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    width: TABLE_RESERVATION_COLUMN_WIDTHS.ageCategories,
                                    flexShrink: 0,
                                    textAlign: "left",
                                }}
                            >
                                {t("Age categories")}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    width: TABLE_RESERVATION_COLUMN_WIDTHS.room,
                                    flexShrink: 0,
                                    textAlign: "left",
                                }}
                            >
                                {t("Room")}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    width: TABLE_RESERVATION_COLUMN_WIDTHS.preferences,
                                    flexShrink: 0,
                                    textAlign: "left",
                                }}
                            >
                                {t("Preferences")}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    width: TABLE_RESERVATION_COLUMN_WIDTHS.status,
                                    flexShrink: 0,
                                    textAlign: "left",
                                }}
                            >
                                {t("Status")}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    width: TABLE_RESERVATION_COLUMN_WIDTHS.time,
                                    flexShrink: 0,
                                    textAlign: "left",
                                }}
                            >
                                {t("Time")}
                            </Typography>
                            <Box
                                sx={{
                                    width: TABLE_RESERVATION_COLUMN_WIDTHS.actions,
                                    flexShrink: 0,
                                }}
                            />
                        </Stack>
                        <Stack gap={1} width="100%" flex={1}>
                            {reservations.map((reservation) => (
                                <DraggableReservationRow
                                    key={reservation.reservationId}
                                    reservation={reservation}
                                    tableId={tableId}
                                    onClickEdit={() => onClickEdit?.(reservation.restaurantReservation)}
                                />
                            ))}
                        </Stack>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ViewTableReservationsDialog;
